/* eslint-disable */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  createFilterOptions,
  CircularProgress,
  useTheme,
} from '@mui/material';

import ButtonWithSpinner from '../ButtonWithSpinner';
import ContactModal from '../ContactModal';

import toastError from '../../toasts/toastError';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import { FormatMask } from '../../utils/FormatMask';

const filter = createFilterOptions({ trim: true });

const ForwardMessageModal = ({ messages, onClose, modalOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const format = new FormatMask();

  const [optionsContacts, setOptionsContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [newContact, setNewContact] = useState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [messageSending, setMessageSending] = useState('');
  const [signMessage, setSignMessage] = useState(true);

  const inputRef = useRef(null);

  useEffect(() => {
    if (modalOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen || searchParam == '') return setOptionsContacts([]);

    setLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get('contacts', { params: { searchParam } });
        setOptionsContacts(data.contacts);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleForwardMessage = useCallback(
    async (contactL) => {
      setSending(true);
      for (const message of messages) {
        try {
          setMessageSending(message.id);
          await api.post('/message/forward', {
            messageId: message.id,
            contactId: contactL.id,
            signMessage,
          });
          await new Promise((r) => setTimeout(r, 900));
        } catch (error) {
          toastError(error);
        }
      }
      setSending(false);
      navigate('/tickets');
    },
    [messages, signMessage, navigate],
  );

  const handleSelectOption = useCallback((e, newValue) => {
    if (!newValue) {
      setSelectedContact(null);
      return;
    }
    if (newValue?.number) {
      setSelectedContact(newValue);
      setSearchParam('');
      setOptionsContacts([]);
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
      setSearchParam('');
      setOptionsContacts([]);
    }
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    setSearchParam('');
    setSelectedContact(null);
    setOptionsContacts([]);
    setSending(false);
  }, [onClose]);

  const handleCloseContactModal = useCallback(() => {
    setContactModalOpen(false);
  }, []);

  const renderOption = useCallback((props, optionL) => {
    const { key, ...restProps } = props;
    return (
      <li key={key} {...restProps}>
        {optionL.number
          ? `${optionL.name} - ${format.setPhonePatternMask(optionL.number)}`
          : `Nenhum contato encontrado com o nome ${optionL.name}`}
      </li>
    );
  }, []);

  const renderOptionLabel = useCallback((optionL) => {
    if (optionL.number) return `${optionL.name} - ${optionL.number}`;
    return optionL.name;
  }, []);

  const createAddContactOption = useCallback((filterOptions, params) => {
    if (params.inputValue.length == 0) return [];

    const filtered = filter(filterOptions, params);

    if (params.inputValue !== '' && filtered.length === 0) {
      filtered.push({ name: params.inputValue });
    }

    return filtered;
  }, []);

  const listboxSx = useMemo(
    () => ({
      padding: 1,
      backgroundColor: theme.custom.colors.defaultBackground,
      color: theme.custom.colors.text,
      '& .MuiAutocomplete-option': {
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
        '&[aria-selected="true"]': { backgroundColor: 'rgba(255,255,255,0.2)' },
      },
    }),
    [theme],
  );

  return (
    <>
      <ContactModal
        open={contactModalOpen}
        initialValues={newContact}
        onClose={handleCloseContactModal}
      />
      <Dialog open={modalOpen} onClose={handleClose}>
        <DialogTitle id='form-dialog-title' sx={theme.custom.dialogColor}>
          Encaminhar mensagem
        </DialogTitle>
        <DialogContent dividers sx={theme.custom.dialogColor}>
          <Autocomplete
            options={optionsContacts}
            sx={{ width: 400 }}
            open={optionsContacts.length > 0 && searchParam !== ''}
            freeSolo
            clearOnBlur
            autoHighlight
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={handleSelectOption}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t('newTicketModal.fieldLabel')}
                variant='outlined'
                autoFocus
                inputRef={inputRef}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchParam(value);
                  if (value === '') setOptionsContacts([]);
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading && searchParam !== '' && searchParam !== null ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                sx={theme.custom.textField}
              />
            )}
            ListboxProps={{ sx: listboxSx }}
          />
        </DialogContent>
        <DialogActions sx={theme.custom.dialogColor}>
          {sending && (
            <>
              <CircularProgress color='inherit' size={20} />
              <Typography variant='body1' color='textSecondary'>
                Enviando {messageSending}...
              </Typography>
            </>
          )}
          <FormControlLabel
            sx={{ marginRight: 7, color: 'gray' }}
            label={i18n.t('messagesInput.signMessage')}
            labelPlacement='start'
            control={
              <Switch
                size='small'
                checked={signMessage}
                onChange={(e) => setSignMessage(e.target.checked)}
                name='showAllTickets'
                sx={theme.custom.switch}
              />
            }
          />
          <ButtonWithSpinner
            variant='outlined'
            type='button'
            disabled={!selectedContact || sending}
            onClick={() => handleForwardMessage(selectedContact)}
            loading={loading}
            sx={theme.custom.confirmButton}
          >
            Encaminhar
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ForwardMessageModal;
